import { Route, createBrowserRouter, createRoutesFromElements, Navigate, defer } from "react-router-dom";
import MusicTesting from "./pages/MusicTesting";
import PlaylistAutopilot from "./pages/PlaylistAutopilot";
import SongSearch from "./pages/SongSearch";
import MusicTestingReports from "./pages/MusicTestingReports/MusicTestingReports";
import Settings from "./pages/Settings/Settings";
import AuthLayout from "./auth/AuthLayout";
import Layout from "./pages/Layout";
import Login from "./pages/Login/Login";
import { axiosInstance, getLatestBlogs } from "./auth/Auth";
import ForgotPassword from "./pages/Login/ForgotPassword";
import LoginForm from "./pages/Login/LoginForm";
import ResetPassword from "./pages/Login/ResetPassword";
import NotFound from "./pages/NotFound/NotFound";
import Error from "./pages/Error/Error";
import Predictor from "./pages/Predictor";
import MinuteByMinute from "./pages/MinuteByMinute";
import ErrorBoundary from "./components/ErrorBoundary";
import CompetitorChart from "./pages/CompetitorChart";
import MusicCharts from "./pages/MusicCharts";
import HybridHitlist from "./pages/HybridHitlist";
import MusicAlerts from "./pages/MusicAlerts";
import Health from "./pages/Health";
import FreestyleDeltas from "./pages/FreestyleDeltas";
import ScheduleCheck from "./pages/ScheduleCheck";
import MusicReports from "./pages/MusicReports";
import Comparison from "./pages/Comparison";
import Ratings from "./pages/Ratings";
import Dashboard from "./pages/Dashboard";
import Clocks from "./pages/Clocks";
import SettingsPassword from "./pages/Settings/SettingsPassword";
import SettingsDayparts from "./pages/Settings/SettingsDayparts";
import VirtualAssistant from "./pages/VirtualAssitant";
import Welcome from "./pages/Welcome";
import { INACTIVE_TIMEOUT } from "./components/constants";
import ContentTesting from "./pages/ContentTesting";
import TSL from "./pages/TSL";
import SettingsPlaylistAutopilot from "./pages/Settings/SettingsPlaylistAutopilot";
import TwoFAVerify from "./pages/Login/TwoFAVerify";

function getRadios() {
    return axiosInstance.get("users/current/radios/?mtt_only=false");
}

function getModules() {
    return axiosInstance.get("users/current/modules/");
}

function getRadiosModuleSettings() {
    return axiosInstance.get("radios/module_settings/");
}

function getRadiosRecordSettings() {
    return axiosInstance.get("radios/record_settings/");
}

function getCompetitorRadios() {
    return axiosInstance.get("users/current/competitor_radios/");
}

function getAllRadioNames() {
    return axiosInstance.get("radios/names/");
}

function getCustomPlaylistAutopilotRadios() {
    return axiosInstance.get("/playlist_autopilot/custom/radios/");
}

function getPlaylistAutopilotSongCategories() {
    return axiosInstance.get("playlist_autopilot/categories/");
}

function getHybridHitlists() {
    return axiosInstance.get("/hybrid_hitlist/hitlists/");
}

function getHybridHitlistLatestData() {
    return axiosInstance.get("/hybrid_hitlist/hybrid/latest_data/");
}
function getDigitalHitlistLatestData() {
    return axiosInstance.get("/hybrid_hitlist/digital/latest_data/");
}

function getLatestCalculatedData() {
    return axiosInstance.get("/users/current/radios/latest_calculated_data/");
}

function getUserInfo() {
    return axiosInstance.get("users/current/");
}

function getRemainingBackupCodes() {
    return axiosInstance.get("2fa/remaining_codes");
}

function updateLastLogin() {
    return axiosInstance.patch("users/current/update_last_login/");
}

function getMusicTestDayparts() {
    return axiosInstance.get(`users/current/dayparts/music_test/`);
}

function getDemographics() {
    return axiosInstance.get(`demographics/`);
}

function getRadioTags() {
    return axiosInstance.get("users/current/tags/");
}

function getAirplayMarkets() {
    return axiosInstance.get(`/users/current/airplay_markets/`);
}

function getCompetitorMarkets() {
    return axiosInstance.get(`/users/current/competitor_markets/`);
}

function getMusicTestingReports() {
    return axiosInstance.get("/reports/music_testing/current_user/");
}

function getMusicReports() {
    return axiosInstance.get("/reports/music/current_user/");
}

function getMusicAlerts() {
    return axiosInstance.get("/reports/music_alert/current_user/");
}

function getContentGroups() {
    return axiosInstance.get("/content_testing/content_groups/");
}

function getFreestyleDeltas() {
    return axiosInstance.get("/freestyle_deltas/current_user/");
}

function getDayparts() {
    return axiosInstance.get("/users/current/dayparts/");
}

function getCustomContentGroups() {
    return axiosInstance.get("/users/current/content_groups/");
}

async function musicTestingReportsLoader({ params }) {
    return defer({ reports: getMusicTestingReports() });
}

async function musicAlertsLoader({ params }) {
    return defer({ reports: getMusicAlerts() });
}

async function musicReportsLoader({ params }) {
    return defer({ reports: getMusicReports(), dayparts: getDayparts(), latestCalculatedData: getLatestCalculatedData() });
}

async function freestyleDeltasLoader({ params }) {
    return defer({ freestyleDeltas: getFreestyleDeltas() });
}

async function clocksLoader({ params }) {
    return defer({ freestyleDeltas: getFreestyleDeltas(), contentGroups: getContentGroups() });
}

async function playlistAutopilotLoader() {
    return defer({ customPlaylistAutopilotRadios: getCustomPlaylistAutopilotRadios(), songCategories: getPlaylistAutopilotSongCategories() });
}

async function hybridHitlistLoader() {
    return defer({ hitlists: getHybridHitlists(), latestHybridData: getHybridHitlistLatestData(), latestDigitalData: getDigitalHitlistLatestData() });
}

async function healthLoader() {
    return defer({ dayparts: getDayparts(), latestCalculatedData: getLatestCalculatedData() });
}

async function daypartsLoader() {
    return defer({ dayparts: getDayparts() });
}

async function contentTestingLoader() {
    return defer({
        contentGroups: getContentGroups(),
        dayparts: getDayparts(),
        freestyleDeltas: getFreestyleDeltas(),
        latestCalculatedData: getLatestCalculatedData(),
    });
}

async function playlistAutopilotSettingsLoader() {
    return defer({ customContentGroups: getCustomContentGroups() });
}

async function dashboardLoader() {
    return defer({ contentGroups: getContentGroups(), dayparts: getDayparts() });
}
async function welcomeLoader() {
    return defer({ latestBlogs: getLatestBlogs(), latestCalculatedData: getLatestCalculatedData() });
}
async function ratingsLoader() {
    return defer({ dayparts: getDayparts(), latestCalculatedData: getLatestCalculatedData() });
}

async function musicChartsLoader() {
    return defer({ dayparts: getDayparts(), latestCalculatedData: getLatestCalculatedData() });
}

async function minuteByMinuteLoader() {
    return defer({ dayparts: getDayparts(), latestCalculatedData: getLatestCalculatedData() });
}

async function competitorChartLoader() {
    return defer({ dayparts: getDayparts(), latestCalculatedData: getLatestCalculatedData() });
}

async function comparisonLoader() {
    return defer({ dayparts: getDayparts(), latestCalculatedData: getLatestCalculatedData() });
}

async function rootLoader({ params }) {
    if (!localStorage.getItem("access_token")) return null;
    const storedLoginTime = parseInt(localStorage.getItem("lastActiveTime"));
    if (Date.now() - storedLoginTime > INACTIVE_TIMEOUT) throw new Response("Session expired", { status: 401 });
    return defer({
        last_login: updateLastLogin(),
        modules: getModules(),
        radios: getRadios(),
        radiosModuleSettings: getRadiosModuleSettings(),
        radiosRecordSettings: getRadiosRecordSettings(),
        allRadioNames: getAllRadioNames(),
        competitorRadios: getCompetitorRadios(),
        radioTags: getRadioTags(),
        airplayMarkets: getAirplayMarkets(),
        competitorMarkets: getCompetitorMarkets(),
        userInfo: getUserInfo(),
        remainingBackupCodes: getRemainingBackupCodes(),
        musicTestDayparts: getMusicTestDayparts(),
        demographics: getDemographics(),
        contentGroups: getContentGroups(),
        freestyleDeltas: getFreestyleDeltas(),
    });
}

export const router = createBrowserRouter(
    createRoutesFromElements(
        <Route element={<AuthLayout />}>
            <Route element={<Layout />} loader={rootLoader} id={"root"} errorElement={<ErrorBoundary />}>
                <Route path="welcome" element={<Welcome />} loader={welcomeLoader} />
                <Route path="dashboard" element={<Dashboard />} loader={dashboardLoader} />
                <Route path="clocks" element={<Clocks />} loader={clocksLoader} />
                <Route path="listeners">
                    <Route path="health" element={<Health />} loader={healthLoader} />
                    <Route path="ratings" element={<Ratings />} loader={ratingsLoader} />
                    <Route path="tsl" element={<TSL />} loader={ratingsLoader} />
                    <Route path="freestyle_deltas" element={<FreestyleDeltas />} loader={freestyleDeltasLoader} />
                    <Route path="content_testing" element={<ContentTesting />} loader={contentTestingLoader} />
                </Route>
                <Route path="music">
                    <Route path="music_charts" element={<MusicCharts />} loader={musicChartsLoader} />
                    <Route path="competitor_chart" element={<CompetitorChart />} loader={competitorChartLoader} />
                    <Route path="comparison" element={<Comparison />} loader={comparisonLoader} />
                    <Route path="minute_by_minute" element={<MinuteByMinute />} loader={minuteByMinuteLoader} />
                    <Route path="song_search" element={<SongSearch />} loader={freestyleDeltasLoader} />
                    <Route path="music_testing" element={<MusicTesting />} />
                    <Route path="playlist_autopilot" element={<PlaylistAutopilot />} loader={playlistAutopilotLoader} />
                    <Route path="predictor" element={<Predictor />} loader={freestyleDeltasLoader} />
                    <Route path="schedule_check" element={<ScheduleCheck />} />
                    <Route path="hybrid_hitlist" element={<HybridHitlist />} loader={hybridHitlistLoader} />
                </Route>
                <Route path="reports">
                    <Route path="music_alerts" element={<MusicAlerts />} loader={musicAlertsLoader} />
                    <Route path="music_reports" element={<MusicReports />} loader={musicReportsLoader} />
                    <Route path="music_testing_reports" element={<MusicTestingReports />} loader={musicTestingReportsLoader} />
                    <Route path="virtual_assistant" element={<VirtualAssistant />} />
                </Route>
                <Route path="settings" element={<Settings />}>
                    <Route index element={<Navigate to={"password"} />} />
                    <Route path="password" element={<SettingsPassword />} />
                    <Route path="dayparts" element={<SettingsDayparts />} loader={daypartsLoader} errorElement={<ErrorBoundary />} />
                    <Route
                        path="playlist_autopilot"
                        element={<SettingsPlaylistAutopilot />}
                        loader={playlistAutopilotSettingsLoader}
                        errorElement={<ErrorBoundary />}
                    />
                </Route>
            </Route>
            <Route path="login" element={<Login />}>
                <Route index element={<LoginForm />} />
                <Route path="forgot_password" element={<ForgotPassword />} />
                <Route path="2fa-verify" element={<TwoFAVerify />} />
                <Route path="reset_password/:token" element={<ResetPassword />} />
            </Route>
            <Route path="error" element={<Error />} />
            <Route path="/" element={<Navigate to={"welcome"} />} />
            <Route path="*" element={<NotFound />} />
        </Route>,
    ),
    { basename: "/client" },
);
